import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box
} from "@mui/material";
import { fetchJson } from "../utils/fetchJson";
import { useTranslation } from "react-i18next";

export default function SchoolFormModal({ open, onClose, onSave, school }) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  const [formValues, setFormValues] = useState({
    name: "",
    address: "",
    adminName: "",
    adminPhone: "",
    adminEmail: "",
    region: "",
    town: "",
    schoolType: "",
    founderName: "",
    founderNationalId: "",
  });
  const [phaseCodes, setPhaseCodes] = useState(["nur_pri", "sec"]);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (school) {
      setFormValues({
        name: school.name || "",
        address: school.address || "",
        adminName: school.adminName || "",
        adminPhone: school.adminPhone || "",
        adminEmail: school.adminEmail || "",
        region: school.region || "",
        town: school.town || "",
        schoolType: school.schoolType || "",
        founderName: school.founderName || "",
        founderNationalId: school.founderNationalId || "",
      });

      const codes = Array.isArray(school.phases)
        ? school.phases.map((p) => p.code).filter(Boolean)
        : [];
      setPhaseCodes(codes.length ? codes : ["nur_pri", "sec"]);
    } else {
      setFormValues({
        name: "",
        address: "",
        adminName: "",
        adminPhone: "",
        adminEmail: "",
        region: "",
        town: "",
        schoolType: "",
        founderName: "",
        founderNationalId: "",
      });
      setPhaseCodes(["nur_pri", "sec"]);
    }
  }, [school, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const togglePhase = (code) => {
    setPhaseCodes((prev) => (
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    ));
  };

  const handleSubmit = async () => {
    setError("");

    // Validate required fields
    if (!formValues.name || !formValues.adminName || !formValues.adminEmail) {
      setError(tr("schoolsPage.requiredFields", "All fields are required"));
      return;
    }

    // Validate email format (trim whitespace first)
    const trimmedEmail = formValues.adminEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError(tr("schoolsPage.invalidEmail", "Invalid email format"));
      return;
    }

    if (!Array.isArray(phaseCodes) || phaseCodes.length === 0) {
      setError(tr("schoolsPage.selectPhase", "Please select at least one phase"));
      return;
    }

    setIsSaving(true);
    try {
      const payload = { ...formValues, adminEmail: trimmedEmail, phaseCodes };
      if (school && school.id) {
        // Edit mode
        await fetchJson(`/schools/${school.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Add mode
        await fetchJson("/schools", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify(payload),
        });
      }
      onSave();
      onClose();
    } catch (e) {
      const details =
        (e && e.data && (e.data.details || e.data.error))
          ? (e.data.details || e.data.error)
          : (e && e.message ? e.message : null);
      const baseMsg = tr("schoolsPage.saveError", "Failed to save school");
      setError(details ? `${baseMsg}: ${details}` : baseMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", py: 2 }}>
        {school ? (t("schoolsPage.editSchool") || "Edit School") : (t("schoolsPage.addNewSchool") || "Add New School")}
      </DialogTitle>
      <DialogContent sx={{ py: 3, px: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <TextField
          label={`${tr("schoolsPage.schoolName", "School Name")} *`}
          fullWidth
          name="name"
          value={formValues.name}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        <TextField
          label={tr("schoolsPage.address", "Address")}
          fullWidth
          name="address"
          value={formValues.address}
          onChange={handleChange}
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          {t("schoolsPage.phases") || "Phases"}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={phaseCodes.includes("nur_pri")}
                  onChange={() => togglePhase("nur_pri")}
                />
              }
              label={tr("schoolsPage.nurPri", "Nursery/Primary")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={phaseCodes.includes("sec")}
                  onChange={() => togglePhase("sec")}
                />
              }
              label={tr("schoolsPage.secondary", "Secondary")}
            />
          </Grid>
        </Grid>

        {/* Location */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label={tr("schoolsPage.region", "Region")}
              fullWidth
              name="region"
              value={formValues.region}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={tr("schoolsPage.town", "Town / City")}
              fullWidth
              name="town"
              value={formValues.town}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        {/* School Credentials */}
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          {tr("schoolsPage.credentials", "School Credentials")}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{tr("schoolsPage.schoolType", "School Type")}</InputLabel>
              <Select
                value={formValues.schoolType}
                label={tr("schoolsPage.schoolType", "School Type")}
                onChange={(e) => setFormValues((prev) => ({ ...prev, schoolType: e.target.value }))}
              >
                <MenuItem value="private">{tr("schoolsPage.typePrivate", "Private (Lay)")}</MenuItem>
                <MenuItem value="confessional">{tr("schoolsPage.typeConfessional", "Confessional")}</MenuItem>
                <MenuItem value="public">{tr("schoolsPage.typePublic", "Public")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={tr("schoolsPage.founderName", "Founder / Proprietor")}
              fullWidth
              size="small"
              name="founderName"
              value={formValues.founderName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={tr("schoolsPage.founderNationalId", "Founder National ID (CNI)")}
              fullWidth
              size="small"
              name="founderNationalId"
              value={formValues.founderNationalId}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          {tr("schoolsPage.schoolAdministrator", "School Administrator")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={`${tr("schoolsPage.adminName", "Admin Name")} *`}
              fullWidth
              name="adminName"
              value={formValues.adminName}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={`${tr("schoolsPage.adminEmail", "Admin Email")} *`}
              fullWidth
              name="adminEmail"
              value={formValues.adminEmail}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={tr("schoolsPage.adminPhone", "Admin Phone")}
              fullWidth
              name="adminPhone"
              value={formValues.adminPhone}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "center" }}>
        <Button 
          onClick={onClose}
          sx={{ width: 120, mr: 2 }}
          disabled={isSaving}
        >
          {tr("schoolsPage.cancel", "Cancel")}
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          sx={{ width: 180 }}
          disabled={isSaving}
        >
          {isSaving ? (
            <CircularProgress size={24} />
          ) : (
            school ? tr("schoolsPage.saveChanges", "Save Changes") : tr("schoolsPage.addNewSchool", "Add School")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}